<template>
  <div id="app" class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Navigation -->
    <nav class="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between h-16">
          <div class="flex items-center">
            <!-- Logo -->
            <div class="flex-shrink-0 flex items-center">
              <h1 class="text-xl font-bold text-gradient">E-Stocks Monitor</h1>
            </div>
            
            <!-- Navigation Links -->
            <div class="hidden md:ml-6 md:flex md:space-x-8">
              <router-link
                to="/"
                class="nav-link"
                :class="{ 'nav-link-active': $route.name === 'Dashboard' }"
              >
                Dashboard
              </router-link>
              <router-link
                to="/tickers"
                class="nav-link"
                :class="{ 'nav-link-active': $route.name === 'Tickers' }"
              >
                Tickers
              </router-link>
              <router-link
                to="/alerts"
                class="nav-link"
                :class="{ 'nav-link-active': $route.name === 'Alerts' }"
              >
                Alerts
              </router-link>
              <router-link
                to="/strategies"
                class="nav-link"
                :class="{ 'nav-link-active': $route.name === 'Strategies' }"
              >
                Strategies
              </router-link>
              <router-link
                to="/orders"
                class="nav-link"
                :class="{ 'nav-link-active': $route.name === 'Orders' }"
              >
                Orders
              </router-link>
              <router-link
                to="/positions"
                class="nav-link"
                :class="{ 'nav-link-active': $route.name === 'Positions' }"
              >
                Positions & Trading
              </router-link>
              <router-link
                to="/manual-order"
                class="nav-link"
                :class="{ 'nav-link-active': $route.name === 'ManualOrder' }"
              >
                Manual Order
              </router-link>
              <router-link
                v-if="brokerStore.isEtrade"
                to="/etrade-auth"
                class="nav-link"
                :class="{ 'nav-link-active': $route.name === 'ETradeAuth' }"
              >
                E*TRADE Auth
              </router-link>
              <router-link
                to="/settings"
                class="nav-link"
                :class="{ 'nav-link-active': $route.name === 'Settings' }"
              >
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                Settings
              </router-link>
            </div>
          </div>
          
          <!-- Right side -->
          <div class="flex items-center space-x-4">
            <!-- Connection Status -->
            <div class="flex items-center space-x-2 hidden">
              <div
                :class="[
                  'w-2 h-2 rounded-full',
                  connectionStore.isConnected ? 'bg-success-500' : 'bg-danger-500'
                ]"
              ></div>
              <span class="text-sm text-gray-600 dark:text-gray-400">
                {{ connectionStore.isConnected ? 'Connected' : 'Disconnected' }}
              </span>
            </div>

            <!-- Logout Button -->
            <button
              @click="handleLogout"
              class="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
              title="Logout"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
              </svg>
            </button>
            
            <!-- Market Status -->
            <div class="flex items-center space-x-2">
              <span class="text-sm text-gray-600 dark:text-gray-400">Market:</span>
              <span
                :class="[
                  'text-sm font-medium',
                  marketStore.isOpen ? 'text-success-600' : 'text-danger-600'
                ]"
              >
                {{ marketStore.isOpen ? 'Open' : 'Closed' }}
              </span>
            </div>
            
            <!-- Mobile menu button -->
            <div class="md:hidden">
              <button
                @click="mobileMenuOpen = !mobileMenuOpen"
                class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
              >
                <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Mobile menu -->
      <div v-show="mobileMenuOpen" class="md:hidden">
        <div class="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-gray-50 dark:bg-gray-700">
          <router-link
            to="/"
            class="mobile-nav-link"
            @click="mobileMenuOpen = false"
          >
            Dashboard
          </router-link>
          <router-link
            to="/tickers"
            class="mobile-nav-link"
            @click="mobileMenuOpen = false"
          >
            Tickers
          </router-link>
          <router-link
            to="/alerts"
            class="mobile-nav-link"
            @click="mobileMenuOpen = false"
          >
            Alerts
          </router-link>
          <router-link
            to="/strategies"
            class="mobile-nav-link"
            @click="mobileMenuOpen = false"
          >
            Strategies
          </router-link>
          <router-link
            to="/orders"
            class="mobile-nav-link"
            @click="mobileMenuOpen = false"
          >
            Orders
          </router-link>
          <router-link
            to="/positions"
            class="mobile-nav-link"
            @click="mobileMenuOpen = false"
          >
            Positions & Trading
          </router-link>
          <router-link
            to="/manual-order"
            class="mobile-nav-link"
            @click="mobileMenuOpen = false"
          >
            Manual Order
          </router-link>
          <router-link
            v-if="brokerStore.isEtrade"
            to="/etrade-auth"
            class="mobile-nav-link"
            @click="mobileMenuOpen = false"
          >
            E*TRADE Auth
          </router-link>
          <router-link
            to="/settings"
            class="mobile-nav-link"
            @click="mobileMenuOpen = false"
          >
            ⚙️ Settings
          </router-link>
        </div>
      </div>
    </nav>
    
    <!-- Main Content -->
    <main class="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <router-view />
    </main>
    
    <!-- Footer -->
    <footer class="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 mt-12">
      <div class="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center">
          <p class="text-sm text-gray-500 dark:text-gray-400">
            © 2025 E-Stocks Trading Monitor. All rights reserved.
          </p>
          <div class="flex space-x-4 text-sm text-gray-500 dark:text-gray-400">
            <span>Version 1.0.0</span>
            <span>•</span>
            <span>Last updated: {{ lastUpdateTime }}</span>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useConnectionStore } from '@/stores/connection'
import { useMarketStore } from '@/stores/market'
import { useAuthStore } from '@/stores/auth'
import { useBrokerStore } from '@/stores/broker'

const router = useRouter()
const connectionStore = useConnectionStore()
const marketStore = useMarketStore()
const authStore = useAuthStore()
const brokerStore = useBrokerStore()

const mobileMenuOpen = ref(false)

const lastUpdateTime = computed(() => {
  return new Date().toLocaleTimeString()
})

const handleLogout = () => {
  authStore.logout()
  router.push('/login')
}

onMounted(() => {
  // Load active broker so the UI can hide broker-specific sections
  brokerStore.load()

  // Initialize WebSocket connection
  connectionStore.connect()

  // Initialize market data
  marketStore.fetchMarketStatus()
})
</script>

<style scoped>
.nav-link {
  @apply inline-flex items-center px-1 pt-1 border-b-2 border-transparent text-sm font-medium text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-colors duration-200;
}

.nav-link-active {
  @apply border-primary-500 text-gray-900 dark:text-white;
}

.mobile-nav-link {
  @apply block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-600 transition-colors duration-200;
}
</style>
